import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { callBackend } from '../../utils/api';
import { uppercaseLabel } from '../../styles/typography';
import {
    secondaryActionButtonSx,
    secondarySectionFrameSx,
} from '../../styles/secondaryMsiStyles';

const EMPTY_FORM = Object.freeze({
    display_name: '',
    kind: 'company',
    status: 'active',
    access_starts_at: '',
    access_ends_at: '',
    seat_limit: '1',
    primary_contact_name: '',
    primary_contact_email: '',
    status_reason_code: '',
});

const toInputDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const toApiDateTime = (value) => (value ? new Date(value).toISOString() : null);

const formatDateTime = (value) => {
    if (!value) return 'No scheduled end';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
};

const extractApiError = (error, fallback) => {
    const raw = String(error?.message || '');
    const body = raw.includes(' - ') ? raw.slice(raw.indexOf(' - ') + 3) : raw;
    try {
        const parsed = JSON.parse(body);
        return parsed?.detail?.message || parsed?.detail || fallback;
    } catch {
        return body || fallback;
    }
};

export default function CustomerOrganizationsPanel({ organizations, loading, error, onRefresh }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState('');

    const sortedOrganizations = useMemo(() => (
        [...organizations].sort((left, right) => (
            String(left?.display_name || '').localeCompare(String(right?.display_name || ''))
        ))
    ), [organizations]);

    const openCreate = () => {
        const now = new Date();
        setEditing(null);
        setForm({ ...EMPTY_FORM, access_starts_at: toInputDateTime(now) });
        setActionError('');
        setDialogOpen(true);
    };

    const openEdit = (organization) => {
        setEditing(organization);
        setForm({
            display_name: organization.display_name || '',
            kind: organization.kind || 'company',
            status: organization.status || 'active',
            access_starts_at: toInputDateTime(organization.access_starts_at),
            access_ends_at: toInputDateTime(organization.access_ends_at),
            seat_limit: organization.seat_limit == null ? '' : String(organization.seat_limit),
            primary_contact_name: organization.primary_contact_name || '',
            primary_contact_email: organization.primary_contact_email || '',
            status_reason_code: organization.status_reason_code || '',
        });
        setActionError('');
        setDialogOpen(true);
    };

    const submit = async () => {
        setSubmitting(true);
        setActionError('');
        try {
            const payload = {
                display_name: form.display_name.trim(),
                kind: form.kind,
                status: form.status,
                access_starts_at: toApiDateTime(form.access_starts_at),
                access_ends_at: toApiDateTime(form.access_ends_at),
                seat_limit: form.kind === 'internal' && form.seat_limit === ''
                    ? null
                    : Number(form.seat_limit),
                primary_contact_name: form.primary_contact_name.trim() || null,
                primary_contact_email: form.primary_contact_email.trim() || null,
            };
            if (editing) {
                payload.status_reason_code = form.status_reason_code.trim() || null;
                const unchanged = {
                    display_name: String(editing.display_name || '').trim(),
                    kind: editing.kind || 'company',
                    status: editing.status || 'active',
                    access_starts_at: toInputDateTime(editing.access_starts_at),
                    access_ends_at: toInputDateTime(editing.access_ends_at),
                    seat_limit: editing.seat_limit == null ? null : Number(editing.seat_limit),
                    primary_contact_name: String(editing.primary_contact_name || '').trim() || null,
                    primary_contact_email: String(editing.primary_contact_email || '').trim() || null,
                    status_reason_code: String(editing.status_reason_code || '').trim() || null,
                };
                Object.entries({
                    display_name: form.display_name.trim(),
                    kind: form.kind,
                    status: form.status,
                    access_starts_at: form.access_starts_at,
                    access_ends_at: form.access_ends_at,
                    seat_limit: payload.seat_limit,
                    primary_contact_name: payload.primary_contact_name,
                    primary_contact_email: payload.primary_contact_email,
                    status_reason_code: payload.status_reason_code,
                }).forEach(([field, value]) => {
                    if (value === unchanged[field]) delete payload[field];
                });
                await callBackend(`admin/customer-organizations/${editing.id}`, 'PATCH', payload);
            } else {
                await callBackend('admin/customer-organizations', 'POST', payload);
            }
            setDialogOpen(false);
            await onRefresh();
        } catch (submitError) {
            setActionError(extractApiError(submitError, 'Unable to save the customer organization.'));
        } finally {
            setSubmitting(false);
        }
    };

    const formValid = Boolean(
        form.display_name.trim()
        && form.access_starts_at
        && (form.kind === 'internal' || /^\d+$/.test(form.seat_limit)),
    );

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                    Manage application access dates, status, and seat capacity.
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Tooltip title="Refresh organizations">
                        <span>
                            <IconButton disabled={loading} onClick={() => onRefresh()}>
                                <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<AddBusinessOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={{ ...uppercaseLabel({ fontSize: '0.74rem' }), ...secondaryActionButtonSx }}
                    >
                        Add organization
                    </Button>
                </Box>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {actionError && !dialogOpen && <Alert severity="error">{actionError}</Alert>}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
            ) : (
                <TableContainer sx={{ ...secondarySectionFrameSx, overflowX: 'auto' }}>
                    <Table size="small" aria-label="Customer organizations">
                        <TableHead>
                            <TableRow>
                                <TableCell>Organization</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Access end</TableCell>
                                <TableCell>Capacity</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedOrganizations.length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center">No customer organizations found.</TableCell></TableRow>
                            )}
                            {sortedOrganizations.map((organization) => {
                                const used = Number(organization.seat_consuming_members || 0);
                                const reserved = Number(organization.reserved_seats || 0);
                                const complimentary = Number(organization.complimentary_members || 0);
                                return (
                                    <TableRow key={organization.id}>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{organization.display_name}</Typography>
                                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{organization.kind}</Typography>
                                        </TableCell>
                                        <TableCell><Chip size="small" label={organization.status} color={organization.status === 'active' ? 'success' : 'default'} /></TableCell>
                                        <TableCell>{formatDateTime(organization.access_ends_at)}</TableCell>
                                        <TableCell>
                                            {organization.seat_limit == null ? 'Unlimited' : `${used + reserved} / ${organization.seat_limit}`}
                                            {complimentary > 0 ? ` · ${complimentary} complimentary` : ''}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => openEdit(organization)} aria-label={`Edit ${organization.display_name}`}>
                                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Edit customer organization' : 'Add customer organization'}</DialogTitle>
                <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, pt: 1 }}>
                    {actionError && <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>{actionError}</Alert>}
                    <TextField label="Organization name" size="small" value={form.display_name} onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))} sx={{ gridColumn: '1 / -1' }} autoFocus />
                    <TextField select label="Kind" size="small" value={form.kind} onChange={(event) => setForm((current) => ({ ...current, kind: event.target.value }))}>
                        <MenuItem value="company">Company</MenuItem>
                        <MenuItem value="personal">Personal</MenuItem>
                        <MenuItem value="internal">Internal</MenuItem>
                    </TextField>
                    <TextField select label="Status" size="small" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        {editing && <MenuItem value="suspended">Suspended</MenuItem>}
                        {editing && <MenuItem value="ended">Ended</MenuItem>}
                    </TextField>
                    <TextField label="Access starts" type="datetime-local" size="small" value={form.access_starts_at} onChange={(event) => setForm((current) => ({ ...current, access_starts_at: event.target.value }))} InputLabelProps={{ shrink: true }} />
                    <TextField label="Access ends" type="datetime-local" size="small" value={form.access_ends_at} onChange={(event) => setForm((current) => ({ ...current, access_ends_at: event.target.value }))} InputLabelProps={{ shrink: true }} helperText="Leave blank for no scheduled end." />
                    <TextField label="Seat limit" type="number" size="small" value={form.seat_limit} onChange={(event) => setForm((current) => ({ ...current, seat_limit: event.target.value }))} inputProps={{ min: 0 }} helperText={form.kind === 'internal' ? 'Blank means unlimited for internal use.' : undefined} />
                    <TextField label="Primary contact name" size="small" value={form.primary_contact_name} onChange={(event) => setForm((current) => ({ ...current, primary_contact_name: event.target.value }))} />
                    <TextField label="Primary contact email" type="email" size="small" value={form.primary_contact_email} onChange={(event) => setForm((current) => ({ ...current, primary_contact_email: event.target.value }))} />
                    {editing && <TextField label="Status reason code" size="small" value={form.status_reason_code} onChange={(event) => setForm((current) => ({ ...current, status_reason_code: event.target.value.toUpperCase() }))} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={submit} disabled={submitting || !formValid}>
                        {submitting ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

CustomerOrganizationsPanel.propTypes = {
    organizations: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRefresh: PropTypes.func,
};

CustomerOrganizationsPanel.defaultProps = {
    organizations: [],
    loading: false,
    error: '',
    onRefresh: () => {},
};
